/**
 * WEB-1098 — render predicates for the DS 1.10.0 resale treatment
 * (`docs/design-system/1.10.0/reference/resale.html`, DES-98 ruling).
 *
 * Pure and synchronous: each is a function of the server payload alone, never
 * of a measurement or the viewport, so the server render and the hydrated
 * client render always agree.
 */
import type { CtaType } from '@loci/types';

/**
 * Bottom-bar stack threshold (ruling §4, option c). "Get tickets" (11) is the
 * longest label that keeps the lede whole at 320 beside a 48px CTA; anything
 * longer ("Reserve free entry" 18, "Resale on Ticketmaster" 22) stacks.
 */
export const LONG_CTA_LABEL_CHARS = 12;

/**
 * Whether a CTA label is long enough that the ≤767 bottom bar stacks (lede +
 * meta on one row, full-width CTA below). A label-length attribute, never a
 * measurement. Generic — not resale-only.
 */
export function isLongCtaLabel(label: string): boolean {
  return label.length > LONG_CTA_LABEL_CHARS;
}

/** The payload fields {@link resaleLinkHrefOf} reads. */
export interface ResaleLinkInputs {
  ctaType?: CtaType | null;
  ctaHref?: string | null;
  resaleTicketUrl?: string | null;
}

/**
 * The secondary "Resale on Ticketmaster" link's destination, or `null` when
 * the link must not render (ruling §2). It renders only when:
 *
 * - a primary purchase path exists: a `buy` CTA with a live destination (the
 *   box office, the venue site or a primary Ticketmaster listing) — a
 *   sold-out, cancelled, RSVP, free-entry or dead-link row has no path to be
 *   secondary to;
 * - the server knows a resale page (`resaleTicketUrl`, https-gated upstream);
 * - the primary CTA is not itself the resale page (`ctaType !== 'resale'`).
 */
export function resaleLinkHrefOf(event: ResaleLinkInputs): string | null {
  const resalePage = event.resaleTicketUrl?.trim() ?? '';
  if (resalePage.length === 0) return null;
  if (event.ctaType !== 'buy') return null;
  const primaryHref = event.ctaHref?.trim() ?? '';
  if (primaryHref.length === 0) return null;
  return resalePage;
}
